import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeftOutlined, MenuOutlined, UploadOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Checkbox, Col, Form, Input, Modal, Row, Select, Space, Spin, Switch, Tabs, Typography, Upload, message } from 'antd'
import Popconfirm from '../components/Popconfirm'
import * as api from '../lib/api'
import { DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR, resolveBusinessUnitLogoUrl } from './businessUnitTheme'
import { useI18n } from '../i18n/I18nProvider'

const { Title, Text } = Typography
const emptyCategory = { title: '', is_active: true }
const emptyQuestion = { text: '', is_active: true }
function reorderRows (rows, draggedId, targetId) {
  const next = [...rows]
  const fromIndex = next.findIndex(row => row.id === draggedId)
  const toIndex = next.findIndex(row => row.id === targetId)
  if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return rows
  const [moved] = next.splice(fromIndex, 1)
  next.splice(toIndex, 0, moved)
  return next
}

function sortablePayload (rows) {
  return rows.map((row, index) => ({ ...row, sort_order: (index + 1) * 10 }))
}

export default function BusinessUnitEditPage () {
  const { t } = useI18n()
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const isNew = !id || id === 'new'
  const [businessUnit, setBusinessUnit] = useState(null)
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [regionOptions, setRegionOptions] = useState([])
  const [organizationOptions, setOrganizationOptions] = useState([])
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [draggedCategoryId, setDraggedCategoryId] = useState(null)
  const [draggedQuestion, setDraggedQuestion] = useState(null)
  const [categoryModal, setCategoryModal] = useState(null)
  const [questionModal, setQuestionModal] = useState(null)
  const [businessUnitForm] = Form.useForm()
  const [categoryForm] = Form.useForm()
  const [questionForm] = Form.useForm()
  const requestedTab = ['details', 'questions'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'details'
  const activeTab = requestedTab

  const categories = useMemo(() => businessUnit?.question_categories || [], [businessUnit])

  const loadBusinessUnit = useCallback(async () => {
    if (isNew) return
    setLoading(true)
    try {
      const data = await api.getAppraisalTemplate(id)
      setBusinessUnit(data)
      businessUnitForm.setFieldsValue({
        name: data.name,
        organization: data.organization || undefined,
        regions: data.regions || [],
        primary_color: data.primary_color || DEFAULT_PRIMARY_COLOR,
        secondary_color: data.secondary_color || DEFAULT_SECONDARY_COLOR,
        form_config_json: JSON.stringify(data.form_config || {}, null, 2),
        hide_employee_list_from_global_hr: data.hide_employee_list_from_global_hr || false,
        is_active: data.is_active
      })
    } catch (e) {
      message.error(e.message || t('templates.messages.template_load_failed', 'Unable to load template'))
    } finally {
      setLoading(false)
    }
  }, [businessUnitForm, id, isNew, t])

  useEffect(() => {
    Promise.all([api.getRegionOptions(), api.getOrganizationOptions()])
      .then(([regions, organizations]) => {
        setRegionOptions(regions.map(region => ({ value: region.id, label: region.name })))
        setOrganizationOptions(organizations.map(organization => ({ value: organization.id, label: organization.name })))
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (isNew) {
      businessUnitForm.setFieldsValue({
        name: '',
        organization: undefined,
        regions: [],
        primary_color: DEFAULT_PRIMARY_COLOR,
        secondary_color: DEFAULT_SECONDARY_COLOR,
        form_config_json: '{}',
        hide_employee_list_from_global_hr: false,
        is_active: true
      })
      setBusinessUnit(null)
      setLoading(false)
      return
    }
    loadBusinessUnit()
  }, [businessUnitForm, id, isNew, loadBusinessUnit])

  async function saveBusinessUnit () {
    const values = await businessUnitForm.validateFields()
    try {
      values.form_config = JSON.parse(values.form_config_json || '{}')
      delete values.form_config_json
    } catch (e) {
      message.error(t('templates.validation.json', 'Form JSON Configuration must be valid JSON.'))
      return
    }
    setSaving(true)
    try {
      if (isNew) {
        const created = await api.createAppraisalTemplate(values)
        message.success(t('templates.messages.created', 'Template created'))
        navigate(`/appraisal-templates/${created.id}/edit`, { replace: true })
      } else {
        await api.updateAppraisalTemplate(id, values)
        message.success(t('templates.messages.saved', 'Template saved'))
        await loadBusinessUnit()
      }
    } catch (e) {
      message.error(e.message || t('templates.messages.save_failed', 'Unable to save template'))
    } finally {
      setSaving(false)
    }
  }

  async function uploadLogo ({ file, onError, onSuccess }) {
    setUploadingLogo(true)
    try {
      const updated = await api.uploadAppraisalTemplateLogo(id, file)
      setBusinessUnit(updated)
      message.success(t('templates.messages.logo_uploaded', 'Logo uploaded'))
      onSuccess(updated)
    } catch (e) {
      message.error(e.message || t('templates.messages.logo_upload_failed', 'Unable to upload logo'))
      onError(e)
    } finally {
      setUploadingLogo(false)
    }
  }

  function openCategoryModal (category) {
    categoryForm.setFieldsValue(category || emptyCategory)
    setCategoryModal(category || {})
  }

  async function saveCategory () {
    const values = await categoryForm.validateFields()
    setSaving(true)
    try {
      if (categoryModal.id) {
        await api.updateQuestionCategory(categoryModal.id, values)
      } else {
        await api.createQuestionCategory(businessUnit.id, values)
      }
      setCategoryModal(null)
      await loadBusinessUnit()
      message.success(t('templates.messages.category_saved', 'Category saved'))
    } catch (e) {
      message.error(e.message || t('templates.messages.category_save_failed', 'Unable to save category'))
    } finally {
      setSaving(false)
    }
  }

  function openQuestionModal (category, question) {
    questionForm.setFieldsValue(question || emptyQuestion)
    setQuestionModal({ category, question: question || {} })
  }

  async function saveQuestion () {
    const values = await questionForm.validateFields()
    setSaving(true)
    try {
      if (questionModal.question.id) {
        await api.updateQuestion(questionModal.question.id, values)
      } else {
        await api.createQuestion(questionModal.category.id, values)
      }
      setQuestionModal(null)
      await loadBusinessUnit()
      message.success(t('templates.messages.question_saved', 'Question saved'))
    } catch (e) {
      message.error(e.message || t('templates.messages.question_save_failed', 'Unable to save question'))
    } finally {
      setSaving(false)
    }
  }

  async function deleteAndReload (deleteFn, itemId, type) {
    try {
      await deleteFn(itemId)
      await loadBusinessUnit()
      message.success(type === 'category'
        ? t('templates.messages.category_deleted', 'Category deleted')
        : t('templates.messages.question_deleted', 'Question deleted'))
    } catch (e) {
      message.error(e.message || (type === 'category'
        ? t('templates.messages.category_delete_failed', 'Unable to delete category')
        : t('templates.messages.question_delete_failed', 'Unable to delete question')))
    }
  }

  async function applyCategoryOrder (rows) {
    const ordered = sortablePayload(rows)
    setBusinessUnit(prev => ({ ...prev, question_categories: ordered }))
    try {
      await Promise.all(ordered.map(row => api.updateQuestionCategory(row.id, { sort_order: row.sort_order })))
      message.success(t('templates.messages.category_order_saved', 'Category order saved'))
    } catch (e) {
      message.error(e.message || t('templates.messages.category_order_failed', 'Unable to save category order'))
      await loadBusinessUnit()
    }
  }

  async function applyQuestionOrder (categoryId, rows) {
    const ordered = sortablePayload(rows)
    setBusinessUnit(prev => ({
      ...prev,
      question_categories: prev.question_categories.map(category => (
        category.id === categoryId ? { ...category, questions: ordered } : category
      ))
    }))
    try {
      await Promise.all(ordered.map(row => api.updateQuestion(row.id, { sort_order: row.sort_order })))
      message.success(t('templates.messages.question_order_saved', 'Question order saved'))
    } catch (e) {
      message.error(e.message || t('templates.messages.question_order_failed', 'Unable to save question order'))
      await loadBusinessUnit()
    }
  }

  function dropCategory (targetId) {
    if (!draggedCategoryId) return
    const next = reorderRows(categories, draggedCategoryId, targetId)
    setDraggedCategoryId(null)
    if (next !== categories) applyCategoryOrder(next)
  }

  function dropQuestion (targetCategory, targetId) {
    if (!draggedQuestion || draggedQuestion.categoryId !== targetCategory.id) return
    const next = reorderRows(targetCategory.questions || [], draggedQuestion.questionId, targetId)
    setDraggedQuestion(null)
    if (next !== targetCategory.questions) applyQuestionOrder(targetCategory.id, next)
  }

  function changeTab (key) {
    const next = new URLSearchParams(searchParams)
    next.set('tab', key)
    setSearchParams(next, { replace: true })
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size='large' /></div>

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 20 }}>
      <Space style={{ marginBottom: 16 }}>
        <Link to='/dashboard?tab=templates'><Button icon={<ArrowLeftOutlined />}>{t('common.back_to_dashboard', 'Back to Dashboard')}</Button></Link>
      </Space>

      <Title level={2} className='tti-page-title' style={{ marginTop: 0, marginBottom: 16 }}>{isNew ? t('templates.actions.create', 'Create Template') : t('templates.actions.edit_named', 'Edit {{name}}', { name: businessUnit?.name || t('templates.singular', 'Template') })}</Title>

      <Tabs
        activeKey={activeTab}
        onChange={changeTab}
        items={[
          {
            key: 'details',
            label: t('common.details', 'Details'),
            children: (
              <>
                <Card size='small' style={{ marginBottom: 16 }}>
                  <Form form={businessUnitForm} layout='vertical'>
                    <Form.Item name='name' label={t('templates.fields.name', 'Template Name')} rules={[{ required: true, message: t('templates.validation.name_required', 'Template name is required') }]}>
                      <Input placeholder={t('templates.examples.name', 'Vietnam - TTI')} />
                    </Form.Item>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item
                          name='organization'
                          label={t('organization.fields.organization', 'Organization')}
                          extra={t('templates.fields.organization_help', 'Controls which organization-scoped HR admins can see and manage this template.')}
                          rules={[{ required: true, message: t('organization.validation.organization_required', 'Organization is required') }]}
                        >
                          <Select
                            options={organizationOptions}
                            placeholder={t('organization.fields.select_organization', 'Select organization')}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name='regions'
                          label={t('templates.fields.regions', 'Regions')}
                          extra={t('templates.fields.regions_help', 'Controls which region-scoped HR admins can see and use this template.')}
                          rules={[{ required: true, type: 'array', min: 1, message: t('templates.validation.region_required', 'Select at least one region') }]}
                        >
                          <Select
                            mode='multiple'
                            options={regionOptions}
                            placeholder={t('templates.fields.select_regions', 'Select regions')}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Space align='start' style={{ marginBottom: 16 }}>
                      <Upload
                        accept='image/png,image/jpeg,image/gif,image/svg+xml,image/webp'
                        customRequest={uploadLogo}
                        disabled={isNew}
                        maxCount={1}
                        showUploadList={false}
                      >
                        <Button icon={<UploadOutlined />} loading={uploadingLogo} disabled={isNew}>{t('templates.actions.upload_logo', 'Upload Logo')}</Button>
                      </Upload>
                      {isNew && <Text type='secondary'>{t('templates.logo.save_first', 'Save the template before uploading a logo.')}</Text>}
                      {!isNew && businessUnit?.logo_url && (
                        <img src={resolveBusinessUnitLogoUrl(businessUnit.logo_url)} alt='' style={{ height: 36, maxWidth: 120, objectFit: 'contain' }} />
                      )}
                    </Space>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item
                          name='primary_color'
                          label={t('templates.fields.primary_color', 'Primary Color')}
                          rules={[{ pattern: /^#[0-9A-Fa-f]{6}$/, message: t('templates.validation.primary_color', 'Use a hex color like #475254') }]}
                        >
                          <Input type='color' />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name='secondary_color'
                          label={t('templates.fields.secondary_color', 'Secondary Color')}
                          rules={[{ pattern: /^#[0-9A-Fa-f]{6}$/, message: t('templates.validation.secondary_color', 'Use a hex color like #A3AEAC') }]}
                        >
                          <Input type='color' />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name='is_active' label={t('common.active', 'Active')} valuePropName='checked'>
                      <Switch />
                    </Form.Item>
                    <Form.Item
                      name='hide_employee_list_from_global_hr'
                      label={t('templates.fields.hide_employee_list_from_global_hr', 'Hide Employee List from Global HR')}
                      valuePropName='checked'
                      extra={t('templates.fields.hide_employee_list_from_global_hr_help', 'Global HR users can still view the session configuration, but cannot access its employee appraisal list.')}
                    >
                      <Switch />
                    </Form.Item>
                    <Form.Item name='form_config_json' hidden>
                      <Input type='hidden' />
                    </Form.Item>
                    <Button className='tti-btn-primary-grey' loading={saving} onClick={saveBusinessUnit}>{t('templates.actions.save', 'Save Template')}</Button>
                  </Form>
                </Card>

                {isNew && (
                  <Alert type='info' showIcon message={t('templates.questions.save_first', 'Save the template before adding categories and questions.')} />
                )}
              </>
            )
          },
          {
            key: 'questions',
            label: t('templates.questions.title', 'Questions'),
            children: isNew
              ? <Alert type='info' showIcon message={t('templates.questions.save_first', 'Save the template before adding categories and questions.')} />
              : (
                <Card
                  size='small'
                  title={t('templates.questions.title', 'Questions')}
                  extra={<Button size='small' onClick={() => openCategoryModal(null)}>{t('templates.actions.add_category', 'Add Category')}</Button>}
                >
                  <Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>
                    {t('templates.questions.reorder_help', 'Drag categories or questions to reorder them.')}
                  </Text>
                  <Space direction='vertical' style={{ width: '100%' }} size='middle'>
                    {categories.map(category => (
                      <Card
                        key={category.id}
                        size='small'
                        draggable
                        onDragStart={() => setDraggedCategoryId(category.id)}
                        onDragOver={event => event.preventDefault()}
                        onDrop={() => dropCategory(category.id)}
                        title={(
                          <Space>
                            <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />
                            <Text strong>{category.title}</Text>
                            {!category.is_active && <Text type='secondary'>({t('common.inactive', 'Inactive')})</Text>}
                          </Space>
                        )}
                        extra={(
                          <Space size='small'>
                            <Button size='small' onClick={() => openQuestionModal(category, null)}>{t('templates.actions.add_question', 'Add Question')}</Button>
                            <Button size='small' onClick={() => openCategoryModal(category)}>{t('common.edit', 'Edit')}</Button>
                            <Popconfirm title={t('templates.confirm.delete_category', 'Delete category and its questions?')} okButtonProps={{ danger: true, className: 'tti-btn-danger' }} onConfirm={() => deleteAndReload(api.deleteQuestionCategory, category.id, 'category')}>
                              <Button size='small' className='tti-btn-danger'>{t('common.delete', 'Delete')}</Button>
                            </Popconfirm>
                          </Space>
                        )}
                      >
                        <Space direction='vertical' style={{ width: '100%' }}>
                          {(category.questions || []).map(question => (
                            <div
                              key={question.id}
                              draggable
                              onDragStart={event => {
                                event.stopPropagation()
                                setDraggedQuestion({ categoryId: category.id, questionId: question.id })
                              }}
                              onDragOver={event => event.preventDefault()}
                              onDrop={event => {
                                event.stopPropagation()
                                dropQuestion(category, question.id)
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '8px 10px',
                                border: '1px solid #f0f0f0',
                                borderRadius: 4,
                                background: 'var(--neutral-white)'
                              }}
                            >
                              <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />
                              <Text style={{ flex: 1 }}>{question.text}</Text>
                              {!question.is_active && <Text type='secondary'>{t('common.inactive', 'Inactive')}</Text>}
                              <Button size='small' onClick={() => openQuestionModal(category, question)}>{t('common.edit', 'Edit')}</Button>
                              <Popconfirm title={t('templates.confirm.delete_question', 'Delete question?')} okButtonProps={{ danger: true, className: 'tti-btn-danger' }} onConfirm={() => deleteAndReload(api.deleteQuestion, question.id, 'question')}>
                                <Button size='small' className='tti-btn-danger'>{t('common.delete', 'Delete')}</Button>
                              </Popconfirm>
                            </div>
                          ))}
                          {(category.questions || []).length === 0 && <Text type='secondary'>{t('templates.questions.empty', 'No questions yet.')}</Text>}
                        </Space>
                      </Card>
                    ))}
                    {categories.length === 0 && <Text type='secondary'>{t('templates.categories.empty', 'No categories yet.')}</Text>}
                  </Space>
                </Card>
                )
          }
        ].filter(Boolean)}
      />

      <Modal
        title={categoryModal?.id ? t('templates.actions.edit_category', 'Edit Category') : t('templates.actions.add_category', 'Add Category')}
        open={!!categoryModal}
        onCancel={() => setCategoryModal(null)}
        onOk={saveCategory}
        okButtonProps={{ loading: saving }}
      >
        <Form form={categoryForm} layout='vertical'>
          <Form.Item name='title' label={t('templates.fields.category_title', 'Category Title')} rules={[{ required: true, message: t('templates.validation.category_title_required', 'Category title is required') }]}>
            <Input placeholder={t('templates.examples.category_title', 'Category 1: Quality of Work')} />
          </Form.Item>
          <Form.Item name='is_active' label={t('common.active', 'Active')} valuePropName='checked'>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={questionModal?.question?.id ? t('templates.actions.edit_question', 'Edit Question') : t('templates.actions.add_question', 'Add Question')}
        open={!!questionModal}
        onCancel={() => setQuestionModal(null)}
        onOk={saveQuestion}
        okButtonProps={{ loading: saving }}
      >
        <Form form={questionForm} layout='vertical'>
          <Form.Item name='text' label={t('templates.fields.question', 'Question')} rules={[{ required: true, message: t('templates.validation.question_required', 'Question is required') }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name='is_active' label={t('common.active', 'Active')} valuePropName='checked'>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
